import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnalyticsHubSummary, AnalyticsHubPlatformBreakdown, AnalyticsHubChannelBreakdown } from '@/types/analytics';
import { formatCompactNumber, formatPercent, formatDelta } from '@/utils/formatNumber';
import { PLATFORMS } from '@/components/ui/PlatformSelect';

/**
 * AnalyticsCompare Component
 *
 * Platform Comparison: Bar chart showing reach/engagement per platform
 * Channel Comparison: Bar chart showing reach/engagement per channel
 * Period Comparison: Side-by-side cards comparing current vs previous period
 */

interface AnalyticsCompareProps {
  compareMode: 'platform' | 'period' | 'channel';
  summary: AnalyticsHubSummary;
  previousPeriodSummary: AnalyticsHubSummary;
  platformBreakdown: AnalyticsHubPlatformBreakdown[];
  channelBreakdown: AnalyticsHubChannelBreakdown[];
}

interface ChartDataPoint {
  platform: string;
  impressions: number;
  engagement: number;
  engagementRate: number;
  postsCount: number;
}

interface ChannelChartDataPoint {
  channelName: string;
  impressions: number;
  engagement: number;
  engagementRate: number;
  postsCount: number;
  platform: string;
}

interface CustomBarTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: ChartDataPoint;
  }>;
}

const getPlatformLabel = (platform: string): string => {
  return PLATFORMS.find((p) => p.value === platform)?.label || platform;
};

const CustomBarTooltip = ({ active, payload }: CustomBarTooltipProps) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload as ChartDataPoint;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-sm">
      <p className="font-medium text-gray-900 mb-2">{data.platform}</p>
      <div className="space-y-1">
        <p>
          <span className="text-gray-500">Posts:</span> {data.postsCount}
        </p>
        <p>
          <span className="text-gray-500">Impressions:</span> {formatCompactNumber(data.impressions)}
        </p>
        <p>
          <span className="text-gray-500">Engagement:</span> {formatCompactNumber(data.engagement)}
        </p>
        <p>
          <span className="text-gray-500">Eng. Rate:</span>{' '}
          {data.impressions === 0 && data.engagementRate === 0 ? <span className="text-gray-400">N/A</span> : formatPercent(data.engagementRate)}
        </p>
      </div>
    </div>
  );
};

const CustomChannelTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChannelChartDataPoint }> }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload as ChannelChartDataPoint;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-sm">
      <p className="font-medium text-gray-900 mb-2">{data.channelName}</p>
      <div className="space-y-1">
        <p><span className="text-gray-500">Platform:</span> {getPlatformLabel(data.platform)}</p>
        <p><span className="text-gray-500">Posts:</span> {data.postsCount}</p>
        <p><span className="text-gray-500">Impressions:</span> {formatCompactNumber(data.impressions)}</p>
        <p><span className="text-gray-500">Engagement:</span> {formatCompactNumber(data.engagement)}</p>
        <p>
          <span className="text-gray-500">Eng. Rate:</span>{' '}
          {data.impressions === 0 && data.engagementRate === 0 ? <span className="text-gray-400">N/A</span> : formatPercent(data.engagementRate)}
        </p>
      </div>
    </div>
  );
};

export const AnalyticsCompare = ({
  compareMode,
  summary,
  previousPeriodSummary,
  platformBreakdown,
  channelBreakdown,
}: AnalyticsCompareProps) => {
  const { t } = useTranslation('analytics');

  // Empty state
  const isEmpty =
    compareMode === 'platform'
      ? platformBreakdown.length === 0
      : compareMode === 'channel'
        ? channelBreakdown.length === 0
        : summary.postsCount === 0 && previousPeriodSummary.postsCount === 0;

  if (isEmpty) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <i className="hm hm-graph text-4xl text-gray-300 block mb-3" />
        <p className="text-gray-400">
          {t('empty_state', { defaultValue: 'No data available for comparison' })}
        </p>
      </div>
    );
  }

  // Platform Comparison
  if (compareMode === 'platform') {
    const chartData: ChartDataPoint[] = platformBreakdown.map((p) => ({
      platform: getPlatformLabel(p.platform),
      impressions: p.totalImpressions > 0 ? p.totalImpressions : p.totalReach,
      engagement: p.totalEngagement,
      engagementRate: p.avgEngagementRate,
      postsCount: p.postsCount,
    }));

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
          {t('platform_comparison', { defaultValue: 'Platform Comparison' })}
        </h2>

        {/* Bar Chart */}
        <div className="mb-8">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="platform" tick={{ fontSize: 12 }} stroke="#6b7280" />
              <YAxis tickFormatter={formatCompactNumber} tick={{ fontSize: 12 }} stroke="#6b7280" />
              <Tooltip content={<CustomBarTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="impressions" fill="#9ca3af" name="Impressions" radius={[4, 4, 0, 0]} />
              <Bar dataKey="engagement" fill="#6366f1" name="Engagement" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Platform Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {platformBreakdown.map((p) => (
            <div key={p.platform} className="border border-gray-200 rounded-lg p-3">
              <div className="font-medium text-gray-900 text-sm mb-2">
                {getPlatformLabel(p.platform)}
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                <p>
                  <span className="text-gray-500">Posts:</span> {p.postsCount}
                </p>
                <p>
                  <span className="text-gray-500">
                    {p.totalImpressions > 0 ? 'Impr.:' : p.totalReach > 0 ? 'Reach:' : 'Impr.:'}
                  </span>{' '}
                  {formatCompactNumber(p.totalImpressions > 0 ? p.totalImpressions : p.totalReach)}
                </p>
                <p>
                  <span className="text-gray-500">Eng.:</span>{' '}
                  {formatCompactNumber(p.totalEngagement)}
                </p>
                <p>
                  <span className="text-gray-500">Rate:</span>{' '}
                  {p.totalReach === 0 && p.totalImpressions === 0 ? <span className="text-gray-400">N/A</span> : formatPercent(p.avgEngagementRate)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Channel Comparison
  if (compareMode === 'channel') {
    const chartData: ChannelChartDataPoint[] = channelBreakdown.map((ch) => ({
      channelName: ch.platformUsername || `Channel ${ch.channelId}`,
      impressions: ch.totalImpressions > 0 ? ch.totalImpressions : ch.totalReach,
      engagement: ch.totalEngagement,
      engagementRate: ch.avgEngagementRate,
      postsCount: ch.postsCount,
      platform: ch.platform,
    }));

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
          {t('channel_comparison', { defaultValue: 'Channel Comparison' })}
        </h2>

        {/* Bar Chart */}
        <div className="mb-8">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="channelName" tick={{ fontSize: 12 }} stroke="#6b7280" />
              <YAxis tickFormatter={formatCompactNumber} tick={{ fontSize: 12 }} stroke="#6b7280" />
              <Tooltip content={<CustomChannelTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="impressions" fill="#9ca3af" name="Impressions" radius={[4, 4, 0, 0]} />
              <Bar dataKey="engagement" fill="#6366f1" name="Engagement" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Channel Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {channelBreakdown.map((ch) => {
            const platformInfo = PLATFORMS.find((p) => p.value === ch.platform);
            return (
              <div key={ch.channelId} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  {/* Channel avatar with platform badge */}
                  <div className="relative flex-shrink-0">
                    {ch.channelAvatarUrl ? (
                      <img src={ch.channelAvatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <span
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: platformInfo?.color || '#6b7280' }}
                      >
                        {platformInfo && (
                          <FontAwesomeIcon icon={platformInfo.icon} className="text-white text-xs" />
                        )}
                      </span>
                    )}
                    {ch.channelAvatarUrl && platformInfo && (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white"
                        style={{ backgroundColor: platformInfo.color }}
                      >
                        <FontAwesomeIcon icon={platformInfo.icon} className="text-white text-[7px]" />
                      </span>
                    )}
                  </div>
                  <span className="font-medium text-gray-900 text-sm truncate">
                    {ch.platformUsername || `Channel ${ch.channelId}`}
                  </span>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  <p><span className="text-gray-500">Posts:</span> {ch.postsCount}</p>
                  <p>
                    <span className="text-gray-500">
                      {ch.totalImpressions > 0 ? 'Impr.:' : ch.totalReach > 0 ? 'Reach:' : 'Impr.:'}
                    </span>{' '}
                    {formatCompactNumber(ch.totalImpressions > 0 ? ch.totalImpressions : ch.totalReach)}
                  </p>
                  <p><span className="text-gray-500">Eng.:</span> {formatCompactNumber(ch.totalEngagement)}</p>
                  <p>
                    <span className="text-gray-500">Rate:</span>{' '}
                    {ch.totalReach === 0 && ch.totalImpressions === 0 ? <span className="text-gray-400">N/A</span> : formatPercent(ch.avgEngagementRate)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Period Comparison
  const currentReachOrImpr = summary.totalReach > 0 ? summary.totalReach : summary.totalImpressions;
  const prevReachOrImpr = previousPeriodSummary.totalReach > 0 ? previousPeriodSummary.totalReach : previousPeriodSummary.totalImpressions;
  const reachDelta = formatDelta(currentReachOrImpr, prevReachOrImpr);
  const engagementDelta = formatDelta(
    summary.totalEngagement,
    previousPeriodSummary.totalEngagement
  );
  const engRateDelta = formatDelta(
    summary.avgEngagementRate,
    previousPeriodSummary.avgEngagementRate
  );
  const postsDelta = summary.postsCount - previousPeriodSummary.postsCount;
  const postsDeltaSign = postsDelta > 0 ? '+' : '';
  const reachLabel = summary.totalReach > 0
    ? t('stat_total_reach', { defaultValue: 'Total Reach' })
    : t('stat_total_impressions', { defaultValue: 'Total Impressions' });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
        {t('period_comparison', { defaultValue: 'Period Comparison' })}
      </h2>

      {/* Current vs Previous Periods */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Previous Period */}
        <div className="border border-gray-200 rounded-lg p-4 sm:p-6 bg-gray-50">
          <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 sm:mb-4">
            {t('previous_period', { defaultValue: 'Previous Period' })}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {reachLabel}
              </span>
              <span className="text-sm sm:text-lg font-semibold text-gray-600">
                {formatCompactNumber(prevReachOrImpr)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {t('stat_total_engagement', { defaultValue: 'Total Engagement' })}
              </span>
              <span className="text-sm sm:text-lg font-semibold text-gray-600">
                {formatCompactNumber(previousPeriodSummary.totalEngagement)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {t('stat_avg_engagement_rate', { defaultValue: 'Avg Eng. Rate' })}
              </span>
              <span className="text-sm sm:text-lg font-semibold text-gray-600">
                {formatPercent(previousPeriodSummary.avgEngagementRate)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {t('stat_posts_published', { defaultValue: 'Posts Published' })}
              </span>
              <span className="text-sm sm:text-lg font-semibold text-gray-600">
                {previousPeriodSummary.postsCount}
              </span>
            </div>
          </div>
        </div>

        {/* Current Period */}
        <div className="border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">
            {t('current_period', { defaultValue: 'Current Period' })}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {reachLabel}
              </span>
              <span className="text-sm sm:text-lg font-semibold text-gray-900">
                {formatCompactNumber(currentReachOrImpr)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {t('stat_total_engagement', { defaultValue: 'Total Engagement' })}
              </span>
              <span className="text-sm sm:text-lg font-semibold text-gray-900">
                {formatCompactNumber(summary.totalEngagement)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {t('stat_avg_engagement_rate', { defaultValue: 'Avg Eng. Rate' })}
              </span>
              <span className="text-sm sm:text-lg font-semibold text-gray-900">
                {formatPercent(summary.avgEngagementRate)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {t('stat_posts_published', { defaultValue: 'Posts Published' })}
              </span>
              <span className="text-sm sm:text-lg font-semibold text-gray-900">{summary.postsCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Delta Summary */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4 text-center">
          {t('change_summary', { defaultValue: 'Change Summary' })}
        </h3>
        <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">{summary.totalReach > 0 ? 'Reach:' : 'Impr.:'}</span>
            <span
              className={`font-semibold ${
                reachDelta.isZero
                  ? 'text-gray-500'
                  : reachDelta.isPositive
                    ? 'text-green-600'
                    : 'text-red-600'
              }`}
            >
              {reachDelta.value}
            </span>
          </div>
          <span className="text-gray-300">|</span>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Engagement:</span>
            <span
              className={`font-semibold ${
                engagementDelta.isZero
                  ? 'text-gray-500'
                  : engagementDelta.isPositive
                    ? 'text-green-600'
                    : 'text-red-600'
              }`}
            >
              {engagementDelta.value}
            </span>
          </div>
          <span className="text-gray-300">|</span>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Eng. Rate:</span>
            <span
              className={`font-semibold ${
                engRateDelta.isZero
                  ? 'text-gray-500'
                  : engRateDelta.isPositive
                    ? 'text-green-600'
                    : 'text-red-600'
              }`}
            >
              {engRateDelta.value}
            </span>
          </div>
          <span className="text-gray-300">|</span>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Posts:</span>
            <span
              className={`font-semibold ${
                postsDelta === 0
                  ? 'text-gray-500'
                  : postsDelta > 0
                    ? 'text-green-600'
                    : 'text-red-600'
              }`}
            >
              {postsDeltaSign}
              {postsDelta}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
